import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { VectorLike } from '@use-gpu/core';

import { provide, useMemo } from '@use-gpu/live';
import { ScissorContext, ScissorMode, useCombinedMatrix, useCombinedScissor, useShader, useShaderRefs } from '@use-gpu/workbench';

import { getScissorPlane } from '@use-gpu/wgsl/scissor/scissor-plane.wgsl';

import { vec3, vec4, mat3, mat4 } from 'gl-matrix';

export type ScissorPlaneProps = PropsWithChildren<{
  matrix?: mat4,
  normal?: VectorLike,
  offset?: number,
  channel?: number,

  bias?: number,
  mode?: ScissorMode,
}>;

export const ScissorPlane: LC<ScissorPlaneProps> = (props: ScissorPlaneProps) => {
  const {matrix, mode, normal, offset = 0, channel = -1, bias = 0, children} = props;

  const xform = useCombinedMatrix(matrix);

  const plane = useMemo(() => {

    const n = vec3.clone(normal as vec3);
    const p = vec3.create();

    if (xform) {
      const mn = mat3.normalFromMat4(mat3.create(), xform);

      vec3.transformMat4(p, p, xform);
      vec3.transformMat3(n, n, mn);
    }

    vec3.normalize(n, n);
    const o = offset - vec3.dot(n, p);

    return vec4.fromValues(n[0], n[1], n[2], o);
  }, [xform, normal, offset]);

  const bound = useShader(getScissorPlane, useShaderRefs<number | vec4>(plane, channel, bias));
  const scissor = useCombinedScissor(bound, mode);

  return provide(ScissorContext, scissor, children);
};
